import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import cn from '@/utils/cn';
import SPARowList from '../../shared/SPARowList';
import { shortDate } from './dateRanges';
import { checkLabel } from './reportParams';
import type { CheckReport } from './types';

const INITIAL_ROWS = 6;

interface ReasonListProps {
  check: CheckReport;
  name: string;
}

const footerText = (shown: number, listed: number, count: number) => {
  if (shown < listed) return `Showing ${shown} of ${count.toLocaleString()}`;
  // The server caps the list (Report::REASON_LIMIT); the count is the real total.
  if (listed < count) return `Showing the ${listed} most recent of ${count.toLocaleString()}`;
  return `Showing all ${count}`;
};

const ReasonList = ({ check, name }: ReasonListProps) => {
  const [expanded, setExpanded] = useState(false);
  const [searchParams] = useSearchParams();
  const { count, failures } = check;
  const visible = expanded ? failures : failures.slice(0, INITIAL_ROWS);
  const label = checkLabel(name);
  const showFooter = failures.length > INITIAL_ROWS || failures.length < count;

  // Keeps the report's own params so closing the drawer lands back on the same view.
  const failureHref = (id: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('failure', String(id));
    return `?${next.toString()}`;
  };

  const footer = showFooter && (
    <>
      <span>{footerText(visible.length, failures.length, count)}</span>
      {!expanded && failures.length > INITIAL_ROWS && (
        <button
          className={cn(
            'tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-[0.8125rem]',
            'tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover hover:tw-underline',
          )}
          type="button"
          onClick={() => setExpanded(true)}
        >
          Show all
        </button>
      )}
    </>
  );

  return (
    <section>
      <div className="tw-mb-2 tw-flex tw-items-baseline tw-justify-between tw-gap-3">
        <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-primary">
          {label}
          {' failures'}
        </h3>
        <span className="tw-whitespace-nowrap tw-text-xs tw-text-spa-tertiary">
          {count.toLocaleString()}
          {' · most recent first'}
        </span>
      </div>
      <SPARowList emptyMessage={`No ${label.toLowerCase()} failures in this range.`} footer={footer}>
        {visible.map(failure => (
          <Link
            key={failure.id}
            className={cn(
              'tw-flex tw-items-start tw-justify-between tw-gap-4 tw-text-spa-primary tw-no-underline',
              'hover:tw-text-spa-heading hover:tw-no-underline',
            )}
            to={failureHref(failure.id)}
          >
            <span>{failure.reason}</span>
            <time
              className="tw-shrink-0 tw-pt-0.5 tw-text-xs tw-tabular-nums tw-text-spa-tertiary"
              dateTime={failure.createdAt}
            >
              {shortDate(failure.createdAt)}
            </time>
          </Link>
        ))}
      </SPARowList>
    </section>
  );
};

export default ReasonList;
